/**
 * Hero Slider (E20-1130)
 *
 * A full-width homepage hero that autoplays through promotional slides. Each slide
 * is one view (100% width) on a horizontal scroll-snap track, so mobile swipe and
 * momentum come free from native scrolling. Navigation: pagination dots, prev/next
 * arrows, and left/right arrow keys. A pause/play control stops autoplay; autoplay
 * also holds while the pointer is over the slider, while focus is inside it, and
 * while the tab is hidden — resuming only if the user hasn't explicitly paused.
 * Reduced-motion suppresses autoplay entirely (manual nav still works) and uses
 * instant (non-smooth) scrolling. In the theme editor, selecting a Slide block slides
 * to it and holds autoplay until it's deselected. Listeners are cleaned up via
 * AbortController.
 */
class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('[data-slider-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
    this.controls = this.querySelector('[data-slider-controls]');
    this.prevBtn = this.querySelector('[data-slider-prev]');
    this.nextBtn = this.querySelector('[data-slider-next]');
    this.toggleBtn = this.querySelector('[data-slider-toggle]');

    this.interval = (parseFloat(this.dataset.autoplaySpeed) || 6) * 1000;
    this.userPaused = false; // sticky — set by the pause/play control
    this.autoHold = false; // transient — hover / focus / tab hidden
    this.editorHold = false; // theme editor — a Slide block is selected
    this.timer = null;
    this.ticking = false;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.prevBtn?.addEventListener('click', () => this.go(-1), { signal });
    this.nextBtn?.addEventListener('click', () => this.go(1), { signal });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i), { signal }));
    this.toggleBtn?.addEventListener('click', () => this.togglePlay(), { signal });

    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    this.addEventListener('keydown', (e) => this.onKeydown(e), { signal });

    // Hold autoplay while the pointer is over the slider or focus is inside it.
    this.addEventListener('pointerenter', () => this.hold(true), { signal });
    this.addEventListener('pointerleave', () => this.hold(false), { signal });
    this.addEventListener('focusin', () => this.hold(true), { signal });
    this.addEventListener(
      'focusout',
      () => {
        if (!this.contains(document.activeElement)) this.hold(false);
      },
      { signal }
    );
    document.addEventListener('visibilitychange', () => this.hold(document.hidden), { signal });

    // Theme editor: slide to a selected Slide block and hold autoplay until it's deselected,
    // per Shopify's slideshow guidance. Both events bubble up from the slide (the block).
    this.addEventListener('shopify:block:select', (e) => this.onBlockSelect(e), { signal });
    this.addEventListener('shopify:block:deselect', () => this.onBlockDeselect(), { signal });

    // update() (not just updateDots) so the controls theme is correct on connect
    // even if the browser restored a non-zero scroll position.
    this.update();
    this.play();
  }

  reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  activeIndex() {
    const width = this.track.clientWidth;
    if (!width) return 0;
    return Math.round(this.track.scrollLeft / width);
  }

  scrollToSlide(index) {
    this.track.scrollTo({
      left: index * this.track.clientWidth,
      behavior: this.reduceMotion() ? 'auto' : 'smooth',
    });
  }

  // Relative move, wrapping at the ends so autoplay and arrows loop.
  go(direction) {
    const count = this.slides.length;
    if (count < 2) return;
    const next = (this.activeIndex() + direction + count) % count;
    this.scrollToSlide(next);
    this.restart();
  }

  goTo(index) {
    this.scrollToSlide(index);
    this.restart();
  }

  onKeydown(e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.go(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.go(1);
    }
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  update() {
    const active = this.activeIndex();
    this.updateDots(active);
    this.syncControlsTheme(active);
  }

  updateDots(active = this.activeIndex()) {
    if (this.dots.length === 0) return;
    this.dots.forEach((dot, i) => {
      const isActive = i === active;
      dot.classList.toggle('hero-slider__dot--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  // The controls overlay the active slide's panel — match their theme to its
  // background so the icons and dots stay legible as the background changes.
  syncControlsTheme(active = this.activeIndex()) {
    if (!this.controls) return;
    const theme = this.slides[active]?.dataset.slideTheme;
    if (!theme) return;
    this.controls.classList.toggle('section-theme--dark', theme === 'dark');
    this.controls.classList.toggle('section-theme--light', theme === 'light');
  }

  onBlockSelect(e) {
    const index = this.slides.indexOf(e.target.closest('[data-slider-slide]'));
    if (index === -1) return;
    this.editorHold = true;
    this.stopTimer();
    this.goTo(index);
  }

  onBlockDeselect() {
    this.editorHold = false;
    this.play();
  }

  // Autoplay can run only when enabled, not user-paused, not held (by the shopper or the
  // theme editor), motion allowed, and there's more than one slide.
  canPlay() {
    return (
      this.slides.length > 1 &&
      !this.userPaused &&
      !this.autoHold &&
      !this.editorHold &&
      !this.reduceMotion()
    );
  }

  play() {
    this.stopTimer();
    if (!this.canPlay()) return;
    this.timer = setInterval(() => this.go(1), this.interval);
  }

  stopTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  // Reset the countdown after a manual move so the next slide isn't cut short.
  restart() {
    if (this.timer) this.play();
  }

  hold(shouldHold) {
    this.autoHold = shouldHold;
    if (shouldHold) {
      this.stopTimer();
    } else {
      this.play();
    }
  }

  togglePlay() {
    this.userPaused = !this.userPaused;
    this.classList.toggle('hero-slider--paused', this.userPaused);
    if (this.toggleBtn) {
      this.toggleBtn.setAttribute('aria-pressed', String(this.userPaused));
      const label = this.userPaused
        ? this.toggleBtn.dataset.labelPlay
        : this.toggleBtn.dataset.labelPause;
      if (label) this.toggleBtn.setAttribute('aria-label', label);
    }
    if (this.userPaused) {
      this.stopTimer();
    } else {
      this.play();
    }
  }

  disconnectedCallback() {
    this.stopTimer();
    this.abortController?.abort();
  }
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}
